import React from "react";
import PropTypes from "prop-types";
import Grid from "material-ui/Grid";
import Paper from "material-ui/Paper";
import Button from "material-ui/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle
} from "material-ui/Dialog";
import Input, { InputLabel } from "material-ui/Input";
import { FormControl } from "material-ui/Form";
import AppBar from "material-ui/AppBar";
import Toolbar from "material-ui/Toolbar";
import IconButton from "material-ui/IconButton";
import CloseIcon from "material-ui-icons/Close";
import Slide from "material-ui/transitions/Slide";
import Card from "material-ui/Card";
import DrivingEventDistribution from "./DrivingEventVisualizations/DrivingEventDistribution";
import DrivingGroupCgu from "./DrivingEventVisualizations/DrivingGroupCgu";
import FilterTable from "../../components/FilterTable/FilterTable";
import Typography from "material-ui/Typography";
import MediaCard from "./../../components/MediaCard/MediaCard";
import SummaryCard from "./../../components/SummaryCard/SummaryCard";
import LinearLoader from "./../../components/LinearLoader/LinearLoader";
import UnitsTable from "./../../components/UnitsTable/UnitsTable";
import DatetimePicker from "./../../components/DatetimePicker/DatetimePicker";
import GrowTransition from "./../../components/GrowTransition/GrowTransition";
import AlertMessage from "../../components/AlertMessage/AlertMessage";
import DownloadAs from "../../components/DownloadAs/DownloadAs";
import TagSelector from "../../components/TagSelector/TagSelector";
import formatNumber from "./../../utilities/numberFormatter";
import { formatDateTimeString } from "./../../utilities/datesFormatter";
import Sequence from "./../../utilities/sequence";
import {
  buildTripsTableColumns,
  buildEventsTableColumns,
  buildAnalysisByUnitTableColumns
} from "./builders";
import "react-table/react-table.css";
import "./Driving.css";
import FetchButton from "../../components/FetchButton/FetchButton";
import TripsImage from "./../../images/driving/trayectos.png";
import RiskImage from "./../../images/driving/riesgo.png";
import WearImage from "./../../images/driving/desgaste.png";
import SummaryTripsImage from "./../../images/driving/summary-trips.png";
import SummarySpeedImage from "./../../images/driving/summary-speed.png";
import StartImage from "./../../images/start-marker.png";
import EndImage from "./../../images/end-marker.png";

const DrivingView = props => {
  const {
    lang,
    units,
    firstLocationDate,
    lastLocationDate,
    distanceUnit,
    isLoading,
    generalReport,
    analysisByUnit,
    trips,
    drivingEvents,
    hasData,
    fetchDrivingData,
    canEdit,
    handleInputsConfiguration,
    onBlurInputConfiguration,
    drivingConfiguration,
    saveConfiguration,
    closeConfiguration,
    resetConfigurationValues,
    defaultConfigurationValues,
    openConfiguration,
    isConfigOpen,
    alertMessage,
    isAlertMessageOpen,
    handleCloseAlertMessage,
    toggleSelectionTrips,
    toggleAllSelectedTrips,
    isSelectedTrip,
    selectedTrips,
    isAllTripsSelected,
    downloadExcel,
    downloadPDF,
    generalBehaviorClassSelector
  } = props;

  const generalBehaviorCardClass = generalBehaviorClassSelector();
  const fechaPrimerUbicacion = formatDateTimeString(firstLocationDate);
  const fechaUltimaUbicacion = formatDateTimeString(lastLocationDate);

  let eventsColumns;
  let tripsTableColumns;

  if (hasData) {
    eventsColumns = buildEventsTableColumns(lang);
    tripsTableColumns = buildTripsTableColumns(lang);
  }

  let analysisByUnitTableColumns;
  if (hasData && units.length > 0) {
    analysisByUnitTableColumns = buildAnalysisByUnitTableColumns(lang);
  }

  const sequence = new Sequence(0, 50);
  const duration = 100;

  return (
    <div className="driving">
      {isLoading && <LinearLoader />}
      <div className="root">
        <Grid container spacing={24}>
          <Grid
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            item
            className="grid-item"
          >
            <DownloadAs
              disabled={!(hasData && !isLoading)}
              xls={true}
              pdf={true}
              xlsClick={downloadExcel}
              pdfClick={downloadPDF}
            />
            {canEdit &&
              !isLoading && (
                <Button
                  className="set-values-button"
                  color="primary"
                  variant="raised"
                  onClick={openConfiguration}
                >
                  {lang.ConfigurarValores}
                </Button>
              )}
            <TagSelector />
          </Grid>
          <Grid
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            item
            className="grid-item"
          >
            <UnitsTable units={units} />
          </Grid>
        </Grid>
      </div>

      <div className="root">
        <Grid container spacing={24}>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            className="grid-item"
          >
            <Paper className="margin-top-bottom rounded" elevation={4}>
              <Grid container>
                <Grid item xs={12} sm={12} md={9} lg={9} xl={9}>
                  <DatetimePicker />
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                  <div className="fetch-button-container">
                    <FetchButton
                      disabled={isLoading}
                      onClick={fetchDrivingData}
                      text={lang.Consultar}
                    />
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>

      <div className="root">
        <Grid container spacing={24} />
      </div>

      {hasData && (
        <div className="root">
          <Grid container spacing={24}>
            <Grid
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              item
              className="grid-item"
            >
              <Typography
                variant="headline"
                align="center"
                color="primary"
                className="headline"
              >
                {lang.Resumen}
              </Typography>
            </Grid>
            <Grid
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              item
              className="grid-item"
            >
              <GrowTransition
                in={!isLoading}
                delay={sequence.next()}
                duration={duration}
              >
                <Paper className="summary rounded" elevation={4}>
                  <Grid container spacing={8}>
                    <Grid
                      xs={12}
                      sm={6}
                      md={3}
                      lg={3}
                      xl={3}
                      item
                      className="grid-item"
                    >
                      <SummaryCard
                        title={lang.Inicio}
                        value={fechaPrimerUbicacion[0]}
                        units={fechaPrimerUbicacion[1]}
                        imageUrl={StartImage}
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      sm={6}
                      md={3}
                      lg={3}
                      xl={3}
                      item
                      className="grid-item"
                    >
                      <SummaryCard
                        title={lang.Fin}
                        value={fechaUltimaUbicacion[0]}
                        units={fechaUltimaUbicacion[1]}
                        imageUrl={EndImage}
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      sm={6}
                      md={3}
                      lg={3}
                      xl={3}
                      item
                      className="grid-item"
                    >
                      <SummaryCard
                        title={lang.VelocidadMaxima}
                        value={
                          generalReport.VelocidadMaxima !== null
                            ? formatNumber(generalReport.VelocidadMaxima)
                            : ""
                        }
                        units={`${distanceUnit}/h`}
                        imageUrl={SummarySpeedImage}
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      sm={6}
                      md={3}
                      lg={3}
                      xl={3}
                      item
                      className="grid-item"
                    >
                      <SummaryCard
                        title={lang.DistanciaRecorrida}
                        value={
                          generalReport.DistanciaRecorrida
                            ? formatNumber(generalReport.DistanciaRecorrida)
                            : ""
                        }
                        units={distanceUnit}
                        imageUrl={SummaryTripsImage}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </GrowTransition>
            </Grid>
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={3}
                item
                className="grid-item"
              >
                <MediaCard
                  title={lang.Trayectos}
                  imageUrl={TripsImage}
                  imageTitle={lang.Trayectos}
                  value={
                    selectedTrips.length > 0
                      ? selectedTrips.length
                      : trips.length
                  }
                />
              </Grid>
            </GrowTransition>
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={3}
                item
                className="grid-item"
              >
                <MediaCard
                  className={generalBehaviorCardClass}
                  title={lang.ComportamientoGeneral}
                  imageUrl={TripsImage}
                  imageTitle={lang.ComportamientoGeneral}
                  value={generalReport.ComportamientoGeneral}
                />
              </Grid>
            </GrowTransition>
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={3}
                item
                className="grid-item"
              >
                <MediaCard
                  title={lang.Riesgo}
                  imageUrl={RiskImage}
                  imageTitle={lang.Riesgo}
                  value={generalReport.Riesgo}
                />
              </Grid>
            </GrowTransition>
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={3}
                item
                className="grid-item"
              >
                <MediaCard
                  title={lang.Desgaste}
                  imageUrl={WearImage}
                  imageTitle={lang.Desgaste}
                  value={generalReport.Desgaste}
                />
              </Grid>
            </GrowTransition>
          </Grid>
          <Grid container spacing={24} className="pad-top">
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={12}
                md={6}
                lg={6}
                xl={6}
                item
                className="grid-item"
              >
                <Grid container>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    item
                    className="grid-item"
                  >
                    <Typography
                      variant="headline"
                      align="center"
                      color="primary"
                      className="headline"
                    >
                      {lang.DistribucionEventos}
                    </Typography>
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    item
                    className="grid-item"
                  >
                    <Card
                      style={{
                        height: "300px",
                        display: "flex",
                        justifyContent: "center"
                      }}
                      className="rounded"
                    >
                      <DrivingEventDistribution
                        data={{
                          71: generalReport.Ralenti,
                          130: generalReport.ExcesoVelocidad,
                          133: generalReport.AceleracionIntensa,
                          134: generalReport.FrenadoIntenso,
                          136: generalReport.GiroBrusco
                        }}
                        lang={lang}
                      />
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </GrowTransition>
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid
                xs={12}
                sm={12}
                md={6}
                lg={6}
                xl={6}
                item
                className="grid-item"
              >
                <Grid container>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    item
                    className="grid-item"
                  >
                    <Typography
                      variant="headline"
                      align="center"
                      color="primary"
                      className="headline"
                    >
                      {lang.PuntuacionPorUnidad}
                    </Typography>
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    item
                    className="grid-item"
                  >
                    <Card style={{ height: "300px" }} className="rounded">
                      <DrivingGroupCgu
                        data={analysisByUnit}
                        lang={lang}
                        cguLimit={drivingConfiguration.LimiteCGU}
                      />
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </GrowTransition>
          </Grid>

          <Grid
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            item
            className="grid-item"
          >
            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid container>
                <FilterTable
                  lang={lang}
                  data={trips}
                  columns={tripsTableColumns}
                  defaultPageSize={5}
                  showPagination={true}
                  selectType="checkbox"
                  selection={selectedTrips}
                  toggleSelection={toggleSelectionTrips}
                  toggleAll={toggleAllSelectedTrips}
                  isSelected={isSelectedTrip}
                  isAllSelected={isAllTripsSelected}
                  checkable
                  tableHeader={lang.Trayectos}
                  filterable={true}
                />
              </Grid>
            </GrowTransition>

            <GrowTransition
              in={!isLoading}
              delay={sequence.next()}
              duration={duration}
            >
              <Grid container>
                <FilterTable
                  lang={lang}
                  data={drivingEvents}
                  columns={eventsColumns}
                  defaultPageSize={5}
                  showPagination={true}
                  tableHeader={lang.EventosDriving}
                  filterable={true}
                />
              </Grid>
            </GrowTransition>
            {analysisByUnit.length > 1 && (
              <GrowTransition
                in={!isLoading}
                delay={sequence.next()}
                duration={duration}
              >
                <Grid container>
                  <FilterTable
                    lang={lang}
                    data={analysisByUnit}
                    columns={analysisByUnitTableColumns}
                    defaultPageSize={5}
                    showPagination={true}
                    tableHeader={lang.DescripcionPorUnidad}
                    filterable={true}
                  />
                </Grid>
              </GrowTransition>
            )}
          </Grid>
        </div>
      )}

      <Dialog open={isConfigOpen} fullScreen transition={Transition}>
        <DialogTitle>
          <AppBar>
            <Toolbar>
              <IconButton
                color="inherit"
                onClick={closeConfiguration}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="title" color="inherit" className="flex">
                {lang.ConfigurarValores}
              </Typography>
            </Toolbar>
          </AppBar>
        </DialogTitle>
        <DialogContent>
          <div className="form-control-container">
            <Grid container justify="center" alignItems="center">
              <Grid
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={6}
                item
                className="grid-item"
              >
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <Typography
                    variant="headline"
                    component="h1"
                    align="center"
                    color="primary"
                    className="section-title"
                  >
                    <b>{lang.Riesgo}</b>
                  </Typography>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="RiesgoRalenti">
                      {lang.Ralenti}
                    </InputLabel>
                    <Input
                      id="RiesgoRalenti"
                      type="number"
                      value={drivingConfiguration.RiesgoRalenti}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="RiesgoExcesoVelocidad">
                      {lang.ExcesoVelocidad}
                    </InputLabel>
                    <Input
                      id="RiesgoExcesoVelocidad"
                      type="number"
                      value={drivingConfiguration.RiesgoExcesoVelocidad}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="RiesgoAceleracionIntensa">
                      {lang.AceleracionIntensa}
                    </InputLabel>
                    <Input
                      id="RiesgoAceleracionIntensa"
                      type="number"
                      value={drivingConfiguration.RiesgoAceleracionIntensa}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="RiesgoFrenadoIntenso">
                      {lang.FrenadoIntenso}
                    </InputLabel>
                    <Input
                      id="RiesgoFrenadoIntenso"
                      type="number"
                      value={drivingConfiguration.RiesgoFrenadoIntenso}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="RiesgoGiroBrusco">
                      {lang.GiroBrusco}
                    </InputLabel>
                    <Input
                      id="RiesgoGiroBrusco"
                      type="number"
                      value={drivingConfiguration.RiesgoGiroBrusco}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={6}
                item
                className="grid-item"
              >
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <Typography
                    variant="headline"
                    component="h1"
                    align="center"
                    color="primary"
                    className="section-title"
                  >
                    <b>{lang.Desgaste}</b>
                  </Typography>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="DesgasteRalenti">
                      {lang.Ralenti}
                    </InputLabel>
                    <Input
                      id="DesgasteRalenti"
                      type="number"
                      value={drivingConfiguration.DesgasteRalenti}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="DesgasteExcesoVelocidad">
                      {lang.ExcesoVelocidad}
                    </InputLabel>
                    <Input
                      id="DesgasteExcesoVelocidad"
                      type="number"
                      value={drivingConfiguration.DesgasteExcesoVelocidad}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="DesgasteAceleracionIntensa">
                      {lang.AceleracionIntensa}
                    </InputLabel>
                    <Input
                      id="DesgasteAceleracionIntensa"
                      type="number"
                      value={drivingConfiguration.DesgasteAceleracionIntensa}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="DesgasteFrenadoIntenso">
                      {lang.FrenadoIntenso}
                    </InputLabel>
                    <Input
                      id="DesgasteFrenadoIntenso"
                      type="number"
                      value={drivingConfiguration.DesgasteFrenadoIntenso}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  item
                  className="grid-item"
                >
                  <FormControl className="form-control">
                    <InputLabel htmlFor="DesgasteGiroBrusco">
                      {lang.GiroBrusco}
                    </InputLabel>
                    <Input
                      id="DesgasteGiroBrusco"
                      type="number"
                      value={drivingConfiguration.DesgasteGiroBrusco}
                      onBlur={onBlurInputConfiguration}
                      onChange={handleInputsConfiguration}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                item
                className="grid-item"
              >
                <FormControl>
                  <InputLabel htmlFor="LimiteCGU">{lang.LimiteCgu}</InputLabel>
                  <Input
                    id="LimiteCGU"
                    type="number"
                    value={drivingConfiguration.LimiteCGU}
                    onBlur={onBlurInputConfiguration}
                    onChange={handleInputsConfiguration}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="raised"
            size="small"
            onClick={resetConfigurationValues}
          >
            {lang.Restablecer}
          </Button>
          <Button
            color="primary"
            variant="raised"
            size="small"
            onClick={defaultConfigurationValues}
          >
            {lang.ValoresPorDefecto}
          </Button>
          <Button
            color="primary"
            variant="raised"
            size="small"
            onClick={saveConfiguration}
          >
            {lang.Guardar}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessage
        open={isAlertMessageOpen}
        message={alertMessage}
        handleClose={handleCloseAlertMessage}
      />
    </div>
  );
};

DrivingView.propTypes = {
  lang: PropTypes.object.isRequired,
  units: PropTypes.array,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  distanceUnit: PropTypes.string,
  isLoading: PropTypes.bool,
  analysisByUnit: PropTypes.array,
  generalReport: PropTypes.object,
  trips: PropTypes.array,
  drivingEvents: PropTypes.array,
  hasData: PropTypes.bool,
  fetchDrivingData: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired
};

function Transition(props) {
  return <Slide direction="up" {...props} />;
}

export default DrivingView;



// WEBPACK FOOTER //
// ./src/containers/Driving/DrivingView.js